import { ModelFramework } from '@viamrobotics/web-client/app/mltraining/v1/ml_training';

import type { RegistryItemSummary } from '$lib/registry-item';
import {
  type APITriplet,
  joinResourceTriplet,
  type ModelTriplet,
} from '$lib/resource-type';

import { isBuiltinAPI } from './api-types';
import { ServiceSubtypes } from './services';

export enum KnownMLModelServiceModel {
  TFLiteCPUDeprecated = 'rdk:builtin:tflite_cpu',
  TFLiteCPU = 'viam:mlmodel-tflite:tflite_cpu',
  TritonModule = 'viam:mlmodelservice:triton',
  OnnxCPU = 'viam:mlmodel:onnx-cpu',
  TensorFlowCPU = 'viam:mlmodel:tensorflow-cpu',
  PyTorchCPU = 'viam:mlmodel:torch-cpu',
}

export enum KnownMLModelModuleIDs {
  TFLiteCPU = 'viam:tflite_cpu',
  OnnxCPU = 'viam:onnx-cpu',
  TensorFlowCPU = 'viam:tensorflow-cpu',
  PyTorchCPU = 'viam:torch-cpu',
}

export interface MLModelDeployment {
  serviceModel: string;
  moduleID: string;
}

/**
 * Preferred ml_model service + the module that provides it for a given model
 * framework, used to auto-configure a machine from the registry model page.
 * Returns undefined for frameworks we can't deploy (button is disabled).
 */
export const mlModelDeploymentForFramework = (
  framework: ModelFramework
): MLModelDeployment | undefined => {
  switch (framework) {
    case ModelFramework.TFLITE: {
      return {
        serviceModel: KnownMLModelServiceModel.TFLiteCPU,
        moduleID: KnownMLModelModuleIDs.TFLiteCPU,
      };
    }
    case ModelFramework.ONNX: {
      return {
        serviceModel: KnownMLModelServiceModel.OnnxCPU,
        moduleID: KnownMLModelModuleIDs.OnnxCPU,
      };
    }
    case ModelFramework.TENSORFLOW: {
      return {
        serviceModel: KnownMLModelServiceModel.TensorFlowCPU,
        moduleID: KnownMLModelModuleIDs.TensorFlowCPU,
      };
    }
    case ModelFramework.PYTORCH: {
      return {
        serviceModel: KnownMLModelServiceModel.PyTorchCPU,
        moduleID: KnownMLModelModuleIDs.PyTorchCPU,
      };
    }
    default: {
      return undefined;
    }
  }
};

export const MLModelServiceTypeToAllowedFrameworks: Record<
  string,
  ModelFramework[]
> = {
  [KnownMLModelServiceModel.TFLiteCPUDeprecated]: [ModelFramework.TFLITE],
  [KnownMLModelServiceModel.TFLiteCPU]: [ModelFramework.TFLITE],
  [KnownMLModelServiceModel.TritonModule]: [
    ModelFramework.TENSORFLOW,
    ModelFramework.PYTORCH,
  ],
  [KnownMLModelServiceModel.OnnxCPU]: [ModelFramework.ONNX],
  [KnownMLModelServiceModel.TensorFlowCPU]: [ModelFramework.TENSORFLOW],
  [KnownMLModelServiceModel.PyTorchCPU]: [ModelFramework.PYTORCH],
};

export const isMlModelAPI = (api: APITriplet) =>
  isBuiltinAPI(api, ServiceSubtypes.ML_MODEL);

export const isTFLiteCPU = (model: ModelTriplet): boolean => {
  const modelString = joinResourceTriplet(model);

  return (
    modelString === (KnownMLModelServiceModel.TFLiteCPUDeprecated as string) ||
    modelString === (KnownMLModelServiceModel.TFLiteCPU as string)
  );
};

export const isTensorFlowCPU = (model: ModelTriplet): boolean => {
  const modelString = joinResourceTriplet(model);

  return modelString === (KnownMLModelServiceModel.TensorFlowCPU as string);
};

export const isOnnxCPU = (model: ModelTriplet): boolean => {
  const modelString = joinResourceTriplet(model);

  return modelString === (KnownMLModelServiceModel.OnnxCPU as string);
};

export const filterMLModelsByServiceType = (
  registryItems: RegistryItemSummary[],
  serviceModel: ModelTriplet
) => {
  const allowedFrameworks =
    MLModelServiceTypeToAllowedFrameworks[joinResourceTriplet(serviceModel)];

  return registryItems.filter((item) => {
    if (item.metadata.oneofKind !== 'mlModelMetadata') {
      return false;
    }
    const framework = item.metadata.mlModelMetadata.modelFramework;
    /**
     * Allow the ML model registry item if we don't have an explicit list of
     * allowed frameworks for the service or the item does not specify a framework
     */
    if (
      allowedFrameworks === undefined ||
      framework === ModelFramework.UNSPECIFIED
    ) {
      return true;
    }

    // otherwise filter out models by the frameworks allowed for current service
    return allowedFrameworks.includes(framework);
  });
};
